/**
 * Pure derivations for the action bar: what each button does right now and,
 * when disabled, the plain-language reason shown in its tooltip.
 */
import { MIN_BAG_FOR_EXCHANGE } from '@wordloom/engine';
import type { Coord } from '@wordloom/engine';
import type { PreviewModel } from './preview';

export interface ActionContext {
  gameActive: boolean;
  isMyTurn: boolean;
  online: boolean;
  pending: boolean;
  draftCount: number;
  rackVisibleCount: number;
  preview: PreviewModel;
  bagCount: number;
  opponentName: string;
}

export interface ActionState {
  disabled: boolean;
  /** Why it is disabled (tooltip / aria-describedby); null when enabled. */
  reason: string | null;
  /** Squares to ring when the disabled action is pressed anyway (an invalid move's culprits). */
  cells?: Coord[];
  /** Disabled because the move itself is invalid (not a gate): pressing it anyway is an error. */
  invalidMove?: boolean;
}

/** Disabled reasons shared with the keyboard paths (actions.ts), so both say the same thing. */
export const reasons = {
  gameOver: 'The game is over.',
  offline: 'You’re offline — reconnecting…',
  opponentsTurn: (name: string): string => `It’s ${name}’s turn.`,
  sending: 'Sending your move…',
  noTiles: 'Place tiles on the board first.',
} as const;

const enabled: ActionState = { disabled: false, reason: null };
const off = (reason: string): ActionState => ({ disabled: true, reason });

function turnGate(ctx: ActionContext): ActionState | null {
  if (!ctx.gameActive) return off(reasons.gameOver);
  if (!ctx.online) return off(reasons.offline);
  if (!ctx.isMyTurn) return off(reasons.opponentsTurn(ctx.opponentName));
  if (ctx.pending) return off(reasons.sending);
  return null;
}

export function submitState(ctx: ActionContext): ActionState & { label: string; points: number | null } {
  // Points only for a move that can be played (or is still being checked):
  // an invalid one scores nothing, whatever its hypothetical total.
  const points = ctx.draftCount > 0 && ctx.preview.state !== 'invalid' ? ctx.preview.total : null;
  const label = points != null ? `Play · ${points}` : 'Play';
  const gate = turnGate(ctx);
  if (gate) return { ...gate, label, points };
  if (ctx.draftCount === 0) return { ...off(reasons.noTiles), label, points };
  if (ctx.preview.state === 'invalid') {
    const state = off(ctx.preview.reason ?? 'That move isn’t valid.');
    if (ctx.preview.badCells.length > 0) state.cells = ctx.preview.badCells;
    // The blank-letter prompt is a next step, not a mistake.
    if (ctx.preview.tone !== 'info') state.invalidMove = true;
    return { ...state, label, points };
  }
  return { ...enabled, label, points };
}

export function recallState(ctx: ActionContext): ActionState {
  if (!ctx.gameActive) return off('The game is over.');
  if (ctx.pending) return off('Sending your move…');
  if (ctx.draftCount === 0) return off('No tiles on the board to recall.');
  return enabled;
}

export function shuffleState(ctx: ActionContext): ActionState {
  if (!ctx.gameActive) return off('The game is over.');
  if (ctx.rackVisibleCount < 2) return off('Nothing to shuffle.');
  return enabled;
}

export function exchangeState(ctx: ActionContext): ActionState {
  const gate = turnGate(ctx);
  if (gate) return gate;
  if (ctx.bagCount < MIN_BAG_FOR_EXCHANGE) {
    return off(`Exchanges need at least ${MIN_BAG_FOR_EXCHANGE} tiles in the bag (${ctx.bagCount} left).`);
  }
  return enabled;
}

export function passState(ctx: ActionContext): ActionState {
  return turnGate(ctx) ?? enabled;
}

/**
 * The pass-streak warning for the Pass confirm popover. After my pass the
 * streak is `consecutivePasses + 1`; the game ends when it reaches `passLimit`.
 */
export function passWarning(consecutivePasses: number, passLimit: number): string | null {
  const remaining = passLimit - (consecutivePasses + 1);
  if (remaining <= 0) return 'Passing now ends the game — each player loses the value of their rack.';
  if (consecutivePasses > 0 || remaining <= 2) {
    return `If you both keep passing, the game ends after ${remaining} more ${remaining === 1 ? 'pass' : 'passes'}.`;
  }
  return null;
}
